import { Box, ConfirmationDialog, Spinner, Text } from "@primer/react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { registrationApi, type IRegistrationPeriod } from "../../api/registration/registrationApi";
import { getActivePeriodId, setActivePeriodId } from "../../api/apiClient";
import { useActivePeriodId } from "../../hooks/useActivePeriodId";
import { PageLayout, ComboboxDotDangKy, SaveCountdown } from "../../components";
import { NotifyHelper } from "../../helpers/toast";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAuth } from "../../hooks/useAuth";
import { useIsMobile } from "../../hooks/useIsMobile";
import { isDevSession } from "../../helpers/jwt";
import { hasScheduleConflict } from "../../helpers/scheduleConflict";
import { computeMissingSongHanh } from "../../helpers/songHanh";
import { findPairPartner } from "../../helpers/pairPartner";
import { IClassListItem } from "../../model/respone/classes/IClassListItem";
import { INhomLop } from "../../model/respone/classes/INhomLop";
import { IKyDangKy, IKyDangKyOption } from "../../model/respone/classes/IKyDangKy";
import { IThoiKhoaBieu } from "../../model/respone/classes/IThoiKhoaBieu";
import { IMyRegistration } from "../../model/respone/registration/IMyRegistration";
import { rootActions } from "../../state/actions/rootActions";
import { eReducerStatusBase } from "../../state/reducer-model/eReducerStatusBase";
import { IFinancialSummary } from "../../model/respone/financial/IFinancialSummary";
import { appConst } from "../../AppConst";
import {
    formatCountdown,
    compareClassByTimeThenName,
    formatDateVi,
    formatThu,
    formatTiet,
    getFinancialBlock,
    registerBlockedTooltip,
    sortTkbByThuTiet,
} from "./classesHelpers";
import ClassesPageMobile from "./mobile/ClassesPageMobile";
import FinancialSummaryWidget from "./FinancialSummaryWidget";
import DebtNoticeWidget from "./DebtNoticeWidget";

/**
 * Bọc nút disabled: nhiều trình duyệt không hiện `title` trên button disabled khi hover.
 * <c>gateReason</c>: ngoài giờ đăng ký hoặc chưa tải xong thông tin thời gian từ cổng.
 */
function withLockedRegisterTooltip(
    isLocked: boolean,
    item: IClassListItem,
    isFull: boolean,
    inner: ReactNode,
    gateReason?: string
): ReactNode {
    if (!isLocked) return inner;
    return (
        <span title={registerBlockedTooltip(item, isFull, gateReason)} style={{ display: "inline-block", cursor: "not-allowed" }}>
            {inner}
        </span>
    );
}

/**
 * Render sub-table lớp thực hành (LTH) inline dưới row parent.
 * 1 dòng <tr colSpan=parentColSpan> chứa nested table.
 * onPickPair(parentClassId, childClassId) — đăng ký cặp LT + TH atomic.
 */
const renderSubTable = (
    classes: import("../../model/respone/classes/IClassListItem").IClassListItem[],
    parentClassId: number,
    parentColSpan: number,
    myList: IMyRegistration[],
    pendingClassIds: number[],
    onPickPair: (parentClassId: number, childClassId: number) => void,
    hasTuition: boolean,
    financial: IFinancialSummary | null,
    gateReason?: string
): ReactNode => {
    return (
        <tr key={`sub-lth-${parentClassId}`}>
            <td colSpan={parentColSpan} style={{ padding: 0, background: "#fafbfc" }}>
                <div style={{ padding: "8px 24px" }}>
                    <p style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
                        Lớp thực hành đính kèm
                    </p>
                    <table className="old-table" style={{ marginBottom: 0 }}>
                        <thead>
                            <tr>
                                <th rowSpan={2} style={{ width: 50 }}>STT</th>
                                <th rowSpan={2}>Tên lớp</th>
                                <th colSpan={4}>Thời khóa biểu</th>
                                <th rowSpan={2} style={{ width: 130 }}>Giáo viên</th>
                                <th rowSpan={2} style={{ width: 80 }}>Còn trống</th>
                                {hasTuition && (
                                    <>
                                        <th rowSpan={2} style={{ width: 90 }}>Số tiền</th>
                                        <th rowSpan={2} style={{ width: 90 }}>Miễn giảm</th>
                                        <th rowSpan={2} style={{ width: 90 }}>Phải nộp</th>
                                    </>
                                )}
                                <th rowSpan={2} style={{ width: 80 }}>#</th>
                            </tr>
                            <tr>
                                <th>Thời gian</th>
                                <th>Thứ</th>
                                <th>Tiết</th>
                                <th>Phòng</th>
                            </tr>
                        </thead>
                        <tbody>
                            {classes.flatMap((sub, idx) => {
                                const isFull = sub.slotsLeft <= 0;
                                // Trùng lịch tươi: nested child (listThucHanh/SongHanh) KHÔNG được reducer
                                // cập nhật canRegister sau mỗi lần ĐK → tự so TKB với lớp đã ĐK ở đây.
                                // Backend RegisterPair vẫn là source of truth (CheckChildPairAsync).
                                const conflictsRegistered = myList.some(
                                    r => r.trang_thai === 1 && r.id_lop_tc !== sub.classId
                                        && hasScheduleConflict(sub.thoiKhoaBieus, r.thoiKhoaBieus)
                                );
                                const subLockReason = gateReason
                                    ?? (conflictsRegistered ? "Trùng lịch với lớp đã đăng ký." : undefined);
                                const isLocked = isFull || !sub.canRegister || conflictsRegistered || !!gateReason;
                                const isPending = pendingClassIds.includes(sub.classId);
                                const isAlreadyRegistered = myList.some(
                                    r => r.id_lop_tc === sub.classId && r.trang_thai === 1
                                );
                                const cellsBefore = [
                                    idx + 1,
                                    <Box>
                                        <p style={{ fontWeight: 600, marginBottom: 4 }}>
                                            {sub.ten_lop_hp}
                                        </p>
                                        {sub.hinh_thuc_dang_ky_text && (
                                            <span className="old-badge old-badge-primary">{sub.hinh_thuc_dang_ky_text}</span>
                                        )}
                                    </Box>,
                                ];
                                const cellsAfter = [
                                    isFull ? (
                                        <span className="text-danger">Hết</span>
                                    ) : (
                                        <strong>{sub.slotsLeft}</strong>
                                    ),
                                    ...(hasTuition ? [
                                        <span style={{ display: "block", textAlign: "right" }}>
                                            {(sub.so_tien ?? 0).toLocaleString("vi-VN")}
                                        </span>,
                                        <span style={{ display: "block", textAlign: "right" }}>
                                            {(sub.so_tien_mien_giam ?? 0).toLocaleString("vi-VN")}
                                        </span>,
                                        <span style={{ display: "block", textAlign: "right" }}>
                                            {(sub.so_tien_phai_nop ?? 0).toLocaleString("vi-VN")}
                                        </span>,
                                    ] : []),
                                    (() => {
                                        if (isAlreadyRegistered) return (
                                            <span className="text-success">
                                                <i className="fas fa-check" /> Đã ĐK
                                            </span>
                                        );
                                        if (isPending) return (
                                            <button className="old-btn old-btn-outline-primary" disabled>
                                                <Spinner size="small" />
                                            </button>
                                        );
                                        // FE-side financial gate (strict mode) — ẩn nút Đăng ký khi:
                                        //   1. SV chưa có hồ sơ tài chính.
                                        //   2. Tổng phải nộp sau khi thêm lớp này > số dư.
                                        // Backend EligibilityService vẫn enforce (defense-in-depth).
                                        const finBlock = getFinancialBlock(sub, financial, { isPracticeClass: true });
                                        if (finBlock) return (
                                            <span style={{ color: "#a30f26", fontStyle: "italic", fontSize: 12 }}>
                                                {finBlock}
                                            </span>
                                        );
                                        return withLockedRegisterTooltip(
                                            isLocked,
                                            sub,
                                            isFull,
                                            <button
                                                className="old-btn old-btn-outline-primary"
                                                disabled={isLocked}
                                                onClick={() => onPickPair(parentClassId, sub.classId)}
                                            >
                                                Đăng ký
                                            </button>,
                                            subLockReason
                                        );
                                    })(),
                                ];
                                const hasConflict = !!(sub.iD_lops_TrungLich && sub.iD_lops_TrungLich.length > 0);
                                const highlightClass = isAlreadyRegistered
                                    ? undefined
                                    : (isFull || hasConflict ? "old-row-locked" : undefined);
                                return renderTKBRows(
                                    sub.thoiKhoaBieus,
                                    cellsBefore,
                                    cellsAfter,
                                    `sub-${parentClassId}-${sub.classId}-${idx}`,
                                    highlightClass,
                                    sub.giao_vien
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </td>
        </tr>
    );
};

/**
 * Clone Old `Home/Index.cshtml` — 3 sections cùng 1 page:
 *   [#Registered]  Top — danh sách lớp đã đăng ký + nút "Lưu dữ liệu đăng ký"
 *   [#listSubject] Bottom-left 55% — danh sách học phần đăng ký học (1 row/môn)
 *   [#listDetail]  Bottom-right 50% — danh sách lớp tín chỉ của môn đang chọn
 *
 * Match video hướng dẫn cũ — vị trí nút "Chọn", "Hủy", "Lưu" giữ nguyên.
 *
 * Multi-row TKB rendering: 1 đăng ký có nhiều buổi/tuần → STT/Mã HP/Tên HP/... rowSpan,
 * Thời gian/Thứ/Tiết/Phòng render từng row riêng.
 */

/**
 * Render N row TKB. cellsBefore/cellsAfter rowSpan=N (chỉ render ở row đầu).
 * Cột TKB gồm Thời gian / Thứ / Tiết / Phòng + Giáo viên (per-row vì 1 lớp có thể nhiều GV).
 * classLevelGv là fallback khi tkb.ho_ten thiếu.
 */
const renderTKBRows = (
    tkbs: IThoiKhoaBieu[] | undefined,
    cellsBefore: React.ReactNode[],
    cellsAfter: React.ReactNode[],
    rowKey: string | number,
    rowClassName?: string,
    classLevelGv?: string
): React.ReactNode => {
    const list = tkbs && tkbs.length > 0 ? sortTkbByThuTiet(tkbs) : [{} as IThoiKhoaBieu];
    return list.map((tkb, idx) => (
        <tr key={`${rowKey}-${idx}`} className={rowClassName}>
            {idx === 0 &&
                cellsBefore.map((cell, i) => (
                    <td key={`b${i}`} rowSpan={list.length} className="text-center">
                        {cell}
                    </td>
                ))}
            <td className="text-center">
                {tkb.tu_ngay && tkb.den_ngay ? `${tkb.tu_ngay} - ${tkb.den_ngay}` : "—"}
            </td>
            <td className="text-center">{tkb.thu !== undefined ? formatThu(tkb.thu) : "—"}</td>
            <td className="text-center">{formatTiet(tkb.tiet, tkb.so_tiet)}</td>
            <td className="text-center">{tkb.phong || "—"}</td>
            <td className="text-center">{tkb.ho_ten || classLevelGv || "—"}</td>
            {idx === 0 &&
                cellsAfter.map((cell, i) => (
                    <td key={`a${i}`} rowSpan={list.length} className="text-center">
                        {cell}
                    </td>
                ))}
        </tr>
    ));
};

const ClassesPage = () => {
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    const { user } = useAuth();
    const { status, list, error_message: classesErrorMessage } = useAppSelector(s => s.classes);
    const { myStatus, myList, cancellingClassIds, pendingClassIds, confirmedClassIds, chotDuLieuStatus } =
        useAppSelector(s => s.registration);
    const { openAt, closeAt, enforceTimeWindow, allowEarlyRegistration, status: queueStatus, error_message: queueErrorMessage } =
        useAppSelector(s => s.queue);
    const financial = useAppSelector(s => s.financial.data);

    /** Tick mỗi giây ép re-render → countdown + ranh giới open/close chuyển trạng thái tự động.
     *  Chỉ chạy khi đang trước openAt → tránh setInterval vĩnh viễn suốt phiên đăng ký bình thường. */
    const [, setTick] = useState(0);
    const openTs = openAt ? new Date(openAt).getTime() : null;
    const closeTs = closeAt ? new Date(closeAt).getTime() : null;
    const beforeOpenNow = !!(enforceTimeWindow && openTs != null && !Number.isNaN(openTs) && Date.now() < openTs);
    useEffect(() => {
        if (!beforeOpenNow) return;
        const id = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(id);
    }, [beforeOpenNow]);

    /** Trạng thái cửa sổ thời gian đăng ký — compute mỗi render (rẻ) để boolean cập nhật
     *  ngay khi vượt qua openAt/closeAt mà không cần memo phức tạp.
     *  enforceTimeWindow=false → skip toàn bộ gate (legacy behavior). */
    const now = Date.now();
    const beforeOpen = enforceTimeWindow && openTs != null && !Number.isNaN(openTs) && now < openTs;
    const afterClose = enforceTimeWindow && closeTs != null && !Number.isNaN(closeTs) && now > closeTs;
    const timeWindowReason: string | undefined = beforeOpen
        ? `Chưa đến thời gian đăng ký. Cổng mở lúc ${formatDateVi(openAt)}.`
        : afterClose
            ? `Đã quá thời gian đăng ký. Cổng đóng lúc ${formatDateVi(closeAt)}.`
            : undefined;

    /** Test mode: phiên DevAuth (claim dev_password=true) bỏ qua gate thời gian/hàng chờ ở UI →
     *  nút "Đăng ký" enable kể cả trước openAt. CHỈ khi backend báo allowEarlyRegistration=true
     *  (Enabled && AllowEarlyRegistration && !ReadOnly) → tắt cờ ở appsettings là tắt hẳn chế độ này. */
    const devEarly = isDevSession() && !!allowEarlyRegistration;

    /** Khóa UI đăng ký khi chưa có snapshot thời gian từ GET /queue/status — tránh nút enable sớm hơn backend. */
    const registerGateReason: string | undefined = (() => {
        if (devEarly) return undefined;
        if (!enforceTimeWindow) return undefined;
        if (timeWindowReason) return timeWindowReason;
        if (queueStatus === eReducerStatusBase.is_error) {
            return queueErrorMessage?.trim() || "Không tải được trạng thái hàng chờ. Vui lòng F5 hoặc thử lại sau.";
        }
        if (queueStatus !== eReducerStatusBase.is_completed) {
            return "Đang tải thông tin thời gian đăng ký từ cổng. Vui lòng đợi...";
        }
        const missingWindow =
            !openAt?.trim() || !closeAt?.trim() ||
            openTs == null || Number.isNaN(openTs) ||
            closeTs == null || Number.isNaN(closeTs);
        if (missingWindow) return "Chưa nhận được khung thời gian đăng ký từ cổng. Vui lòng tải lại trang (F5).";
        return undefined;
    })();

    /** Filter: hình thức đăng ký cho cả 2 bảng. 0=tất cả. */
    const [hinhThucReg, setHinhThucReg] = useState<number>(0);  // Registered table filter
    const [hinhThuc, setHinhThuc] = useState<number>(0);        // listSubject filter
    /** Filter: nhóm lớp học phần (Old `select_nhom_lop`). "" = tất cả. Value = ma_nhom. */
    const [nhomLop, setNhomLop] = useState<string>("");
    /** Catalog nhóm lớp đợt hiện tại — load từ Redis `reg:catalog:nhom-lop` qua API. */
    const [nhomLops, setNhomLops] = useState<INhomLop[]>([]);
    /** Filter: đợt học. 0 = tất cả đợt. */
    const [kyDangKy, setKyDangKy] = useState<number>(0);
    /** Catalog đợt học — load từ Redis `reg:catalog:ky-dang-ky` qua API (tên đợt cho combobox). */
    const [kyDangKys, setKyDangKys] = useState<IKyDangKy[]>([]);
    /** Multi-period: danh sách ĐỢT ĐĂNG KÝ đang mở SV thuộc. Đợt đang chọn lấy reactive từ
     *  useActivePeriodId() → tự bám theo khi Hub đổi đợt (adopt / chuyển khi đóng đợt đang xem). */
    const [periods, setPeriods] = useState<IRegistrationPeriod[]>([]);
    const selectedPeriodId = useActivePeriodId();
    /** Môn đang chọn — drives listDetail. */
    /** Dòng học phần đang chọn — định danh theo composite `id_mon|so_tin_chi`, KHÔNG phải id_mon. */
    const [selectedSubjectKey, setSelectedSubjectKey] = useState<string | null>(null);
    /** Confirm dialog state cho hủy đăng ký. */
    const [confirmCancel, setConfirmCancel] = useState<IMyRegistration | null>(null);
    /** Confirm dialog state cho chốt dữ liệu. */
    const [confirmChot, setConfirmChot] = useState(false);
    /** Phase 2: parent class id đang expand sub-table (LTH hoặc SH). null = không expand. */
    const [expandedSub, setExpandedSub] = useState<{ classId: number } | null>(null);

    /** Multi-period: resolve danh sách đợt SV thuộc + chọn đợt (ưu tiên localStorage, fallback đợt đầu).
     *  Set X-Period-Id (qua setActivePeriodId) TRƯỚC khi mọi load classes/my/financial/queue chạy.
     *  Re-run khi selectedPeriodId đổi (vd Hub chuyển đợt khi đóng đợt đang xem) → refresh combobox,
     *  bỏ đợt vừa đóng khỏi danh sách. setActivePeriodId no-op nếu pid không đổi → không vòng lặp. */
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        registrationApi.GetPeriods().then(res => {
            if (cancelled || !res.is_success || !res.data) return;
            setPeriods(res.data);
            const ids = res.data.map(p => p.periodId);
            let pid = getActivePeriodId() ?? 0;
            if (!ids.includes(pid)) pid = ids[0] ?? 0;
            if (pid > 0) {
                setActivePeriodId(pid); // notify → useActivePeriodId() cập nhật selectedPeriodId.
            }
        });
        return () => { cancelled = true; };
    }, [user, selectedPeriodId]);

    /** Luôn gọi /queue/status khi vào trang (deep link /classes, reload) — chờ đã resolve đợt. */
    useEffect(() => {
        if (!user || selectedPeriodId <= 0) return;
        dispatch(rootActions.queue.LOAD_STATUS_START());
    }, [user, selectedPeriodId, dispatch]);

    useEffect(() => {
        if (selectedPeriodId <= 0) return;
        if (status === eReducerStatusBase.is_not_initialization) {
            dispatch(rootActions.classes.LOAD_START());
        }
        if (myStatus === eReducerStatusBase.is_not_initialization) {
            dispatch(rootActions.registration.LOAD_MY_START());
        }
    }, [status, myStatus, selectedPeriodId, dispatch]);

    /** Bắt đầu vòng lặp poll số dư tài chính (mỗi 60s) khi vào trang đăng ký (sau khi resolve đợt). */
    useEffect(() => {
        if (selectedPeriodId <= 0) return;
        dispatch(rootActions.financial.LOAD_START());
    }, [selectedPeriodId, dispatch]);

    /** Đổi đợt đăng ký: set header X-Period-Id mới + reset filter + reload toàn bộ dữ liệu đợt mới. */
    const handleChangePeriod = (pid: number) => {
        if (pid <= 0 || pid === selectedPeriodId) return;
        setActivePeriodId(pid); // notify → useActivePeriodId() cập nhật selectedPeriodId.
        setKyDangKy(0);
        setSelectedSubjectKey(null);
        // Reset state đợt cũ trước khi load đợt mới → tránh flash vị trí/token + list lớp + đăng ký sai đợt.
        dispatch(rootActions.queue.RESET());
        dispatch(rootActions.classes.RESET());
        dispatch(rootActions.registration.RESET());
        dispatch(rootActions.classes.LOAD_START());
        dispatch(rootActions.registration.LOAD_MY_START());
        dispatch(rootActions.financial.LOAD_START());
        dispatch(rootActions.queue.LOAD_STATUS_START());
    };

    /** Fetch nhóm lớp catalog 1 lần lúc mount. Catalog stable trong 1 đợt → không cần refresh.
     *  Local state thay vì Redux vì payload nhỏ + chỉ dùng ở page này. */
    useEffect(() => {
        let cancelled = false;
        registrationApi.GetNhomLops().then(res => {
            if (!cancelled && res.is_success && res.data) {
                setNhomLops(res.data);
            }
        });
        return () => { cancelled = true; };
    }, []);

    /** Fetch catalog đợt học 1 lần lúc mount — chỉ dùng để map tên đợt cho combobox. */
    useEffect(() => {
        let cancelled = false;
        registrationApi.GetKyDangKys().then(res => {
            if (!cancelled && res.is_success && res.data) {
                setKyDangKys(res.data);
            }
        });
        return () => { cancelled = true; };
    }, []);

    

    /** Group lớp theo (id_mon + so_tin_chi) cho listSubject — cùng 1 id_mon nhưng khác số tín
     *  (môn nằm ở các CTDT khác nhau) phải tách thành 2 dòng học phần riêng. */
    const subjects = useMemo(() => {
        const map = new Map<
            string,
            {
                subjectKey: string;
                id_mon: number;
                ten_mon: string;
                ky_hieu: string;
                ky_thu: number | undefined;
                nhom_tu_chon_text: string | undefined;
                nhom_TuChon_Chung: string | undefined;
                rang_buoc_text: string | undefined;
                hinh_thuc_dang_ky: number | undefined;
                hinh_thuc_dang_ky_text: string | undefined;
                tinh_chat_lop_text: string | undefined;
                so_tin_chi: number;
                classes: IClassListItem[];
            }
        >();
        list.forEach(c => {
            const subjectKey = `${c.id_mon}|${c.so_tin_chi}`;
            if (!map.has(subjectKey)) {
                map.set(subjectKey, {
                    subjectKey,
                    id_mon: c.id_mon,
                    ten_mon: c.ten_mon,
                    ky_hieu: c.ky_hieu ?? "",
                    ky_thu: c.ky_thu,
                    nhom_tu_chon_text: c.nhom_tu_chon_text,
                    nhom_TuChon_Chung: c.nhom_TuChon_Chung,
                    rang_buoc_text: c.rang_buoc_text,
                    hinh_thuc_dang_ky: c.hinh_thuc_dang_ky,
                    hinh_thuc_dang_ky_text: c.hinh_thuc_dang_ky_text,
                    tinh_chat_lop_text: c.tinh_chat_lop_text,
                    so_tin_chi: c.so_tin_chi,
                    classes: [],
                });
            }
            map.get(subjectKey)!.classes.push(c);
        });
        // Sắp xếp các lớp trong mỗi học phần theo thời gian (buổi sớm nhất) rồi tên lớp.
        // Mảng `classes` mới tạo trong memo (không phải redux state) → sort tại chỗ an toàn.
        const result = Array.from(map.values());
        result.forEach(s => s.classes.sort(compareClassByTimeThenName));
        return result;
    }, [list]);

    const filteredSubjects = useMemo(() => {
        return subjects
            .filter(s => {
                if (hinhThuc !== 0 && s.hinh_thuc_dang_ky !== hinhThuc) return false;
                if (nhomLop) {
                    const hasNhomLop = s.classes.some(c => String(c.stu_nhomlop_ids ?? 0) === nhomLop);
                    if (!hasNhomLop) return false;
                }
                // Đợt học: giữ môn nếu có ít nhất 1 lớp thuộc đợt đang chọn (0 = tất cả).
                if (kyDangKy !== 0 && !s.classes.some(c => c.ky_dang_ky === kyDangKy)) return false;
                return true;
            })
            // Lọc luôn lớp bên trong môn theo đợt → desktop listDetail (qua selectedSubjectClasses)
            // và mobile SubjectCard chỉ xòe lớp thuộc đợt đang chọn.
            .map(s => kyDangKy === 0 ? s : { ...s, classes: s.classes.filter(c => c.ky_dang_ky === kyDangKy) });
    }, [subjects, hinhThuc, nhomLop, kyDangKy]);

    /**
     * Bảng 4 listDetailWithSubject — flat list class match (nhomLop + hinhThuc) filter.
     * Mỗi class = 1 row trong B4 (môn info duplicated per class). Hiển thị khi user chọn nhomLop.
     */
    const filteredCombinedClasses = useMemo(() => {
        return list.filter(c => {
            if (nhomLop && String(c.stu_nhomlop_ids ?? 0) !== nhomLop) return false;
            if (hinhThuc !== 0 && c.hinh_thuc_dang_ky !== hinhThuc) return false;
            if (kyDangKy !== 0 && c.ky_dang_ky !== kyDangKy) return false;
            return true;
        }).sort(compareClassByTimeThenName);
    }, [list, nhomLop, hinhThuc, kyDangKy]);

    /** Options combobox "Chọn đợt học" — các đợt distinct THỰC SỰ có trong danh sách lớp,
     *  nhãn tra từ catalog ky-dang-ky (`Đợt {dot} - {mô tả}`); fallback `Đợt {n}` nếu catalog lag.
     *  Chỉ hiện combobox khi > 1 đợt. */
    const kyDangKyOptions = useMemo<IKyDangKyOption[]>(() => {
        const present = Array.from(
            new Set(list.map(c => c.ky_dang_ky).filter((k): k is number => !!k && k > 0))
        );
        const byId = new Map(kyDangKys.map(k => [k.ky_dang_ky, k]));
        return present
            .map(k => {
                const meta = byId.get(k);
                const label = meta
                    ? `Đợt ${meta.dot}${meta.mo_ta_chi_tiet ? ` - ${meta.mo_ta_chi_tiet}` : ""}`
                    : `Đợt ${k}`;
                return { ky_dang_ky: k, dot: meta?.dot ?? k, label };
            })
            .sort((a, b) => a.dot - b.dot);
    }, [list, kyDangKys]);
    const showKyDangKyCombo = kyDangKyOptions.length > 1;

    /** Options cho dropdown "Chọn nhóm lớp" — lấy từ catalog Redis (ESS Module A nạp).
     *  Sort theo ten_nhom A→Z cho UX. Value = id nhóm lớp (match c.stu_nhomlop_ids trên class). */
    const nhomLopOptions = useMemo(() => {
        return [...nhomLops].sort((a, b) =>
            a.ten_nhom.localeCompare(b.ten_nhom, "vi", { sensitivity: "base" }));
    }, [nhomLops]);

    const selectedSubject = useMemo(
        () => subjects.find(s => s.subjectKey === selectedSubjectKey) ?? null,
        [subjects, selectedSubjectKey]
    );

    /** Lớp của môn đang chọn sau khi lọc theo đợt — drives listDetail (Bảng 3). */
    const selectedSubjectClasses = useMemo(
        () => {
            if (!selectedSubject) return [];
            return kyDangKy === 0
                ? selectedSubject.classes
                : selectedSubject.classes.filter(c => c.ky_dang_ky === kyDangKy);
        },
        [selectedSubject, kyDangKy]
    );

    /** Đăng ký đã confirmed (Registered table) — filter theo hình thức đăng ký. */
    const registeredList = useMemo(() => {
        const confirmed = myList.filter(r => r.trang_thai === 1);
        if (hinhThucReg === 0) return confirmed;
        return confirmed.filter(r => r.hinh_thuc_dang_ky === hinhThucReg);
    }, [myList, hinhThucReg]);

    const tongTC = useMemo(
        () => registeredList.reduce((sum, r) => sum + (r.so_tin_chi || 0), 0),
        [registeredList]
    );

    /** Đợt có bật học phí theo số dư → 3 cột tiền (Số tiền / Miễn giảm / Phải nộp) LUÔN hiện.
     *  Lớp thiếu đơn giá hoặc TC=0 vẫn hiển thị 0đ — không ẩn cột để SV biết đợt đang bật cờ tài chính.
     *  Cờ off → ẩn hoàn toàn (đợt thường không liên quan tiền). Trước đây dùng proxy
     *  `list.some(so_tien > 0)` — proxy này sai khi mọi lớp thiếu đơn giá: cờ on nhưng 3 cột bị ẩn,
     *  che giấu lỗi setup data thay vì phơi bày. */
    const hasTuition = !!financial?.tinh_hoc_phi_theo_so_du;

    // 2 cột Nhóm chung / Nhóm tự chọn chỉ hiện khi trường KHÁC ICTU (cột Ràng buộc luôn giữ).
    // ICTU không dùng nhóm → ẩn để gọn bảng (multi-tenant qua School.Code).
    const showNhomCols = appConst.schoolCode !== "ICTU";

    // requireChot: đợt yêu cầu SV bấm "Lưu dữ liệu" mới được công nhận (cờ per-đợt
    // yeu_cau_chot_du_lieu). Mặc định true khi financial chưa nạp → giữ hành vi cũ. false →
    // ẩn nút "Lưu dữ liệu" + cảnh báo chưa lưu (đăng ký là đã được công nhận & đồng bộ).
    const requireChot = financial?.yeu_cau_chot_du_lieu !== false;

    const tcMin = user?.so_tc_min_duoc_dk ?? 0;
    const tcMax = user?.so_tc_max_duoc_dk ?? 0;

    // isChotDuLieu derive từ myList (Backend Registrations.is_chot_du_lieu) thay vì user-level flag.
    // Lý do: chốt là per-row trong DB, sync về FE qua /api/registration/my. user object cũ giữ
    // optional field nhưng không còn nguồn truth.
    const chotInfo = useMemo(() => {
        const chotRows = myList.filter(r => r.isChotDuLieu && r.ngayChotDuLieu);
        if (chotRows.length === 0) return { isChotDuLieu: false, ngayChot: null as Date | null };
        const maxIso = chotRows.reduce<string>((m, r) => {
            const v = r.ngayChotDuLieu!;
            return !m || v > m ? v : m;
        }, "");
        return { isChotDuLieu: true, ngayChot: new Date(maxIso) };
    }, [myList]);
    const isChotDuLieu = chotInfo.isChotDuLieu;
    const isChottingNow = chotDuLieuStatus === eReducerStatusBase.is_loading;

    // Song hành (bắc cầu): tập môn song hành bắt buộc còn thiếu so với các môn đã ĐK. Mirror backend
    // ChotDuLieu guard — dùng để cảnh báo + chặn nút "Lưu dữ liệu". Backend vẫn chặn cứng khi chốt.
    const songHanhMissing = useMemo(
        () => computeMissingSongHanh(list, myList.filter(r => r.trang_thai === 1).map(r => r.id_lop_tc)),
        [list, myList]
    );
    const hasMissingSongHanh = songHanhMissing.missingMonIds.length > 0;

    /** Loading flags cho table-level spinner. is_completed/is_error → đã xong; còn lại = loading. */
    const classesLoading =
        status !== eReducerStatusBase.is_completed &&
        status !== eReducerStatusBase.is_error;
    const myLoading =
        myStatus !== eReducerStatusBase.is_completed &&
        myStatus !== eReducerStatusBase.is_error;

    /** 1 row spinner placeholder dùng cho mọi bảng khi loading + chưa có data. */
    const renderLoadingRow = (colSpan: number, label = "Đang tải...") => (
        <tr>
            <td colSpan={colSpan} style={{ textAlign: "center", padding: 20 }}>
                <Spinner size="small" />
                <span style={{ marginLeft: 8, color: "#888" }}>{label}</span>
            </td>
        </tr>
    );

    /** Refresh slot count cho danh sách classIds qua endpoint nhẹ /api/registration/slots. */
    const refreshSlots = async (classIds: number[]) => {
        if (classIds.length === 0) return;
        const res = await registrationApi.GetSlots(classIds);
        if (res.is_success && res.data) {
            dispatch(rootActions.classes.UPDATE_SLOTS(res.data));
        }
    };

    const handleSelectSubject = (subjectKey: string) => {
        setSelectedSubjectKey(prev => {
            const next = prev === subjectKey ? null : subjectKey;
            // Auto refresh slot khi user CHỌN môn (next != null) → cột "Còn trống" Bảng 3 fresh.
            if (next !== null) {
                const subj = subjects.find(s => s.subjectKey === next);
                if (subj) refreshSlots(subj.classes.map(c => c.classId));
            }
            return next;
        });
    };

    /** Manual button "Tải lại" — refresh slot của các lớp đang hiển thị (Bảng 3 hoặc Bảng 4). */
    const handleRefreshSlots = () => {
        const visibleIds = selectedSubject
            ? selectedSubject.classes.map(c => c.classId)
            : filteredCombinedClasses.map(c => c.classId);
        refreshSlots(visibleIds);
    };

    const handleRegister = (classId: number) => {
        dispatch(rootActions.registration.REGISTER_START({ classId }));
    };

    const handleCancelConfirm = () => {
        if (confirmCancel) {
            dispatch(rootActions.registration.CANCEL_START({ classId: confirmCancel.id_lop_tc }));
        }
        setConfirmCancel(null);
    };

    // Click button "Lưu dữ liệu đăng ký": guard min/max trước khi mở dialog confirm.
    // Pattern này tốt hơn chạy guard SAU khi user đã confirm (không phải mở dialog rồi báo lỗi).
    // Mirror Old: check min chặn cứng; max đã chặn ở flow đăng ký nhưng vẫn check defensive.
    const handleChotDuLieuClick = () => {
        if (registeredList.length === 0) {
            NotifyHelper.Error("Bạn chưa đăng ký lớp nào để lưu dữ liệu.");
            return;
        }
        if (tcMin > 0 && tongTC < tcMin) {
            NotifyHelper.Error(`Bạn chưa đăng ký đủ số tín chỉ tối thiểu (${tongTC}/${tcMin} TC).`);
            return;
        }
        if (tcMax > 0 && tongTC > tcMax) {
            NotifyHelper.Error(`Tổng tín chỉ vượt giới hạn tối đa (${tongTC}/${tcMax} TC).`);
            return;
        }
        if (hasMissingSongHanh) {
            NotifyHelper.Error(`Chưa đăng ký đủ môn học song hành bắt buộc: ${songHanhMissing.missingMonNames.join(", ")}.`);
            return;
        }
        setConfirmChot(true);
    };

    // User confirm trong dialog → dispatch saga; saga toast success, reload myList.
    const handleChotDuLieuConfirm = () => {
        dispatch(rootActions.registration.CHOT_DU_LIEU_START());
        setConfirmChot(false);
    };

    /** Đổi nhóm lớp — gom side-effect đang inline ở <select> desktop để bản mobile dùng chung.
     *  Reset môn đang chọn + refresh slot Bảng 4 khi chọn 1 nhóm cụ thể. */
    const handleChangeNhomLop = (value: string) => {
        setNhomLop(value);
        setSelectedSubjectKey(null);
        if (value !== "") {
            const ids = list.filter(c => String(c.stu_nhomlop_ids ?? 0) === value).map(c => c.classId);
            refreshSlots(ids);
        }
    };

    /** Đổi đợt học — reset môn đang chọn vì lớp của môn có thể không thuộc đợt mới. */
    const handleChangeKyDangKy = (value: number) => {
        setKyDangKy(value);
        setSelectedSubjectKey(null);
    };

    /** Toggle expand sub-table lớp thực hành (LTH) — cùng logic với onClick desktop. */
    const handleToggleExpand = (classId: number) => {
        setExpandedSub(prev => (prev?.classId === classId ? null : { classId }));
    };

    /** Đăng ký cặp lớp cha + lớp con (LTH/SH) — cùng action desktop dùng. */
    const handleRegisterPair = (parentClassId: number, childClassId: number) => {
        dispatch(rootActions.registration.REGISTER_PAIR_START({ parentClassId, childClassId }));
    };

    /** Combobox "Chọn đợt đăng ký" (multi-period) — chỉ hiện khi SV thuộc >1 đợt mở. Dùng chung mobile + desktop. */
    const periodSelectorNode = periods.length > 1 ? (
        <Box sx={{ mx: isMobile ? "12px" : 0, mt: isMobile ? "12px" : 0, mb: 2 }}>
            <ComboboxDotDangKy
                periods={periods}
                value={selectedPeriodId}
                onChange={handleChangePeriod}
            />
        </Box>
    ) : null;

    /** Nhánh render mobile (<768px) — tái dùng toàn bộ state/memo/handler ở trên,
     *  chỉ thay tầng giao diện. Desktop giữ nguyên ở phần `return` bên dưới. */
    if (isMobile) {
        return (
            <PageLayout title="Đăng ký tín chỉ" maxWidth="100%" sx={{ p: 0, px: 0, py: 0 }}>
                {periodSelectorNode}
                <Box sx={{ px: "12px", pt: "12px" }}>
                    <DebtNoticeWidget />
                    <FinancialSummaryWidget />
                </Box>
                <ClassesPageMobile
                    enforceTimeWindow={enforceTimeWindow}
                    beforeOpen={beforeOpen}
                    afterClose={afterClose}
                    openAt={openAt}
                    closeAt={closeAt}
                    openTs={openTs}
                    now={now}
                    registerGateReason={registerGateReason}
                    queueStatus={queueStatus}
                    regTime={{
                        // Ưu tiên giờ ĐỢT (openAt/closeAt từ Redis) giống Header — ESS sửa đợt
                        // thì F5 thấy ngay; fallback claim JWT khi đợt chưa có dữ liệu.
                        start: openAt ?? user?.tu_ngay_dkhocdi ?? user?.thoi_gian_bat_dau ?? user?.thoi_gian_bat_dau_toan_truong,
                        end: closeAt ?? user?.den_ngay_dkhocdi ?? user?.thoi_gian_ket_thuc ?? user?.thoi_gian_ket_thuc_toan_truong,
                    }}
                    filteredSubjects={filteredSubjects}
                    filteredCombinedClasses={filteredCombinedClasses}
                    nhomLopOptions={nhomLopOptions}
                    kyDangKyOptions={kyDangKyOptions}
                    showKyDangKyCombo={showKyDangKyCombo}
                    registeredList={registeredList}
                    myList={myList}
                    hinhThuc={hinhThuc}
                    hinhThucReg={hinhThucReg}
                    nhomLop={nhomLop}
                    kyDangKy={kyDangKy}
                    onApplyFilter={(ht, nl, kdk) => { setHinhThuc(ht); handleChangeNhomLop(nl); setKyDangKy(kdk); }}
                    onChangeHinhThucReg={setHinhThucReg}
                    selectedSubjectKey={selectedSubjectKey}
                    onSelectSubject={handleSelectSubject}
                    expandedSub={expandedSub}
                    onToggleExpand={handleToggleExpand}
                    classesLoading={classesLoading}
                    classesError={status === eReducerStatusBase.is_error}
                    classesErrorMessage={classesErrorMessage}
                    myLoading={myLoading}
                    requireChot={requireChot}
                    isChotDuLieu={isChotDuLieu}
                    isChottingNow={isChottingNow}
                    ngayChot={chotInfo.ngayChot}
                    tongTC={tongTC}
                    tcMin={tcMin}
                    tcMax={tcMax}
                    songHanhMissingNames={songHanhMissing.missingMonNames}
                    pendingClassIds={pendingClassIds}
                    confirmedClassIds={confirmedClassIds}
                    cancellingClassIds={cancellingClassIds}
                    onRegister={handleRegister}
                    onPickPair={handleRegisterPair}
                    onRefreshSlots={handleRefreshSlots}
                    onRetryLoad={() => dispatch(rootActions.classes.LOAD_START())}
                    onChotClick={handleChotDuLieuClick}
                    confirmCancel={confirmCancel}
                    setConfirmCancel={setConfirmCancel}
                    onCancelConfirm={handleCancelConfirm}
                    confirmChot={confirmChot}
                    setConfirmChot={setConfirmChot}
                    onChotConfirm={handleChotDuLieuConfirm}
                />
            </PageLayout>
        );
    }

    return (
        <PageLayout title="Đăng ký tín chỉ" maxWidth="100%" sx={{ p: "15px" }}>
            {periodSelectorNode}
            {/* Banner gate theo time window — chỉ hiển thị khi Registration:EnforceTimeWindow=true
                và đang ngoài [openAt, closeAt]. Trùng UX với server-side 403 OUT_OF_TIME_WINDOW. */}
            {beforeOpen && openAt && devEarly && (
                <Box
                    sx={{
                        bg: "#d1ecf1",
                        color: "#0c5460",
                        border: "1px solid #bee5eb",
                        borderRadius: 2,
                        textAlign: "center",
                        py: 2,
                        px: 3,
                        mb: 2,
                        fontWeight: 500,
                        fontSize: 1,
                    }}
                >
                    Chế độ hỗ trợ (DevAuth): cổng chưa mở (giờ mở{" "}
                    <strong>{formatDateVi(openAt)}</strong>) nhưng bạn vẫn đăng ký được để kiểm thử.
                </Box>
            )}
            {beforeOpen && openAt && !devEarly && (
                <Box
                    sx={{
                        bg: "#fff3cd",
                        color: "#856404",
                        border: "1px solid #ffeeba",
                        borderRadius: 2,
                        textAlign: "center",
                        py: 2,
                        px: 3,
                        mb: 2,
                        fontWeight: 500,
                        fontSize: 1,
                    }}
                >
                    Chưa đến thời gian đăng ký. Cổng mở lúc{" "}
                    <strong>{formatDateVi(openAt)}</strong>.
                    {openTs != null && (
                        <> Còn lại <strong>{formatCountdown(openTs - now)}</strong>.</>
                    )}
                    <br />
                    <span style={{ fontSize: 12, fontStyle: "italic" }}>
                        Bạn có thể xem trước danh sách lớp ở bên dưới. Nút "Đăng ký" sẽ được bật khi đến giờ.
                    </span>
                </Box>
            )}
            {afterClose && closeAt && (
                <Box
                    sx={{
                        bg: "#f8d7da",
                        color: "#721c24",
                        border: "1px solid #f5c6cb",
                        borderRadius: 2,
                        textAlign: "center",
                        py: 2,
                        px: 3,
                        mb: 2,
                        fontWeight: 500,
                        fontSize: 1,
                    }}
                >
                    Đã quá thời gian đăng ký. Cổng đóng lúc{" "}
                    <strong>{formatDateVi(closeAt)}</strong>.
                </Box>
            )}

            {enforceTimeWindow && registerGateReason && !beforeOpen && !afterClose && (
                <Box
                    sx={{
                        bg: "#e7f3ff",
                        color: "#004085",
                        border: "1px solid #b8daff",
                        borderRadius: 2,
                        textAlign: "center",
                        py: 2,
                        px: 3,
                        mb: 2,
                        fontWeight: 500,
                        fontSize: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                    }}
                >
                    {(queueStatus === eReducerStatusBase.is_loading ||
                        queueStatus === eReducerStatusBase.is_not_initialization) && (
                        <Spinner size="small" />
                    )}
                    <span>{registerGateReason}</span>
                </Box>
            )}

            {/* Banner lỗi tải danh sách lớp — vd 429 rate-limit ("Bạn đang gửi quá nhiều yêu cầu...").
                Trước đây 429 chỉ làm bảng trống, SV tưởng "không có lớp" → hiện rõ lý do + nút thử lại. */}
            {status === eReducerStatusBase.is_error && (
                <Box
                    sx={{
                        bg: "#f8d7da",
                        color: "#721c24",
                        border: "1px solid #f5c6cb",
                        borderRadius: 2,
                        textAlign: "center",
                        py: 2,
                        px: 3,
                        mb: 2,
                        fontWeight: 500,
                        fontSize: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <span>
                        {classesErrorMessage?.trim() ||
                            "Không tải được danh sách lớp. Vui lòng thử lại sau giây lát."}
                    </span>
                    <button
                        type="button"
                        className="old-btn old-btn-outline-danger"
                        onClick={() => dispatch(rootActions.classes.LOAD_START())}
                    >
                        Tải lại
                    </button>
                </Box>
            )}

            {/* Time info banner top right */}
            {(user?.thoi_gian_bat_dau || user?.thoi_gian_bat_dau_toan_truong) && (
                <Box
                    sx={{
                        textAlign: "right",
                        color: "red",
                        fontSize: 1,
                        mb: 2,
                        lineHeight: 1.6,
                    }}
                >
                    {user?.thoi_gian_bat_dau && user?.thoi_gian_ket_thuc && (
                        <div>
                            Thời gian đăng ký học bắt đầu từ{" "}
                            <strong>{user.thoi_gian_bat_dau}</strong> đến{" "}
                            <strong>{user.thoi_gian_ket_thuc}</strong>
                        </div>
                    )}
                    {user?.thoi_gian_bat_dau_toan_truong && user?.thoi_gian_ket_thuc_toan_truong && (
                        <div>
                            Thời gian đăng ký học toàn trường bắt đầu từ{" "}
                            <strong>{user.thoi_gian_bat_dau_toan_truong}</strong> đến{" "}
                            <strong>{user.thoi_gian_ket_thuc_toan_truong}</strong>
                        </div>
                    )}
                </Box>
            )}

            {/* Banner tài chính — công nợ (check_tai_chinh) + số dư + miễn giảm SV (poll 60s) */}
            <DebtNoticeWidget />
            <FinancialSummaryWidget />

            {/* ===================== SECTION 1: REGISTERED ===================== */}
            <Box id="Registered" sx={{ mb: 4 }}>
                <div className="old-section-title">
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 2,
                            flexWrap: "wrap",
                            flex: 1,
                        }}
                    >
                        <span>Danh sách các lớp tín chỉ đã đăng ký</span>
                        <select
                            value={hinhThucReg}
                            onChange={(e) => setHinhThucReg(Number(e.target.value))}
                            className="old-form-control"
                            style={{ width: "auto", display: "inline-block" }}
                        >
                            <option value={0}>Tất cả hình thức</option>
                            <option value={1}>Học chính khóa</option>
                            <option value={2}>Học lại, học cải thiện điểm, học bù (lớp riêng)</option>
                            <option value={3}>Học lại, học cải thiện điểm, học bù (lớp ghép)</option>
                        </select>
                        <Text
                            sx={{
                                fontSize: 14,
                                color: "red",
                                fontWeight: 500,
                                ml: 1,
                            }}
                        >
                            Số tín chỉ được đăng ký: tối thiểu: <b>{tcMin}</b>, tối đa: <b>{tcMax}</b>, đã đăng ký: <b>{tongTC}</b>
                        </Text>
                    </Box>
                    <Box sx={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                        <SaveCountdown />
                        {requireChot && !isChotDuLieu && (
                            <button
                                className="old-btn old-btn-success old-btn-pulse"
                                onClick={handleChotDuLieuClick}
                                disabled={registeredList.length === 0 || isChottingNow || !!registerGateReason || hasMissingSongHanh}
                                title={
                                    registerGateReason
                                        ? registerGateReason
                                        : hasMissingSongHanh
                                            ? `Chưa đăng ký đủ môn song hành: ${songHanhMissing.missingMonNames.join(", ")}`
                                            : "Lưu dữ liệu đăng ký — sau khi lưu sẽ không thể thay đổi"
                                }
                            >
                                {isChottingNow ? <Spinner size="small" /> : "Lưu dữ liệu đăng ký"}
                            </button>
                        )}
                    </Box>
                </div>

                {/* Cảnh báo thiếu môn song hành (bắc cầu) — chặn Lưu dữ liệu cho đến khi đăng ký đủ */}
                {!isChotDuLieu && hasMissingSongHanh && (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            bg: "#fdecec",
                            border: "1px solid #f5b5b5",
                            borderLeft: "4px solid #d63939",
                            borderRadius: 2,
                            p: "12px 14px",
                            mb: 2,
                        }}
                    >
                        <i
                            className="fas fa-link"
                            style={{ color: "#c92a2a", fontSize: 20, marginTop: 2, flexShrink: 0 }}
                        />
                        <Box>
                            <Text sx={{ display: "block", fontWeight: 700, fontSize: 1, color: "#a61e1e", mb: "2px" }}>
                                Chưa đăng ký đủ môn học song hành bắt buộc
                            </Text>
                            <Text sx={{ display: "block", fontSize: 1, color: "#7a2020", lineHeight: 1.5 }}>
                                Bạn đã đăng ký môn có ràng buộc học song hành. Cần đăng ký thêm:{" "}
                                <strong>{songHanhMissing.missingMonNames.join(", ")}</strong>. Chưa đủ thì{" "}
                                <strong>không thể "Lưu dữ liệu đăng ký"</strong>.
                            </Text>
                        </Box>
                    </Box>
                )}

                {/* Cảnh báo chưa lưu — kết quả chưa được công nhận */}
                {requireChot && !isChotDuLieu && registeredList.length > 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            bg: "#fff8e6",
                            border: "1px solid #ffe2a8",
                            borderLeft: "4px solid #f0a500",
                            borderRadius: 2,
                            p: "12px 14px",
                            mb: 2,
                        }}
                    >
                        <i
                            className="fas fa-triangle-exclamation"
                            style={{ color: "#e8950c", fontSize: 20, marginTop: 2, flexShrink: 0 }}
                        />
                        <Box>
                            <Text sx={{ display: "block", fontWeight: 700, fontSize: 1, color: "#8a5a00", mb: "2px" }}>
                                Chưa lưu — kết quả chưa được công nhận
                            </Text>
                            <Text sx={{ display: "block", fontSize: 1, color: "#6b5012", lineHeight: 1.5 }}>
                                Các lớp bạn đã đăng ký <strong>chưa được lưu</strong>. Hãy bấm{" "}
                                <strong>"Lưu dữ liệu đăng ký"</strong> ở trên. Nếu không lưu, kết quả
                                đăng ký sẽ <strong>không được công nhận và xét duyệt</strong>.
                            </Text>
                        </Box>
                    </Box>
                )}

                {/* Status banner khi đã chốt */}
                {isChotDuLieu && (
                    <Box
                        sx={{
                            bg: "#dc3545",
                            color: "#fff",
                            textAlign: "center",
                            py: 2,
                            mb: 2,
                            fontWeight: 500,
                            fontSize: 1,
                        }}
                    >
                        Bạn đã lưu dữ liệu đăng ký
                        {chotInfo.ngayChot && (
                            <> vào lúc <strong>{chotInfo.ngayChot.toLocaleString("vi-VN", {
                                hour: "2-digit",
                                minute: "2-digit",
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                            })}</strong></>
                        )}.
                        Dữ liệu sau khi lưu sẽ không được thay đổi
                    </Box>
                )}

                <div style={{ overflowX: "auto" }}>
                    <table className="old-table">
                        <thead>
                            <tr>
                                <th rowSpan={2} style={{ width: 50 }}>STT</th>
                                <th rowSpan={2} style={{ width: 110 }}>Mã học phần</th>
                                <th rowSpan={2}>Tên học phần</th>
                                <th rowSpan={2} style={{ width: 80 }}>Số tín chỉ</th>
                                <th rowSpan={2} style={{ width: 130 }}>Tên lớp</th>
                                <th rowSpan={2} style={{ width: 100 }}>Loại lớp</th>
                                <th colSpan={4}>Thời khóa biểu</th>
                                <th rowSpan={2} style={{ width: 130 }}>Giáo viên</th>
                                {hasTuition && (
                                    <>
                                        <th rowSpan={2} style={{ width: 90 }}>Số tiền</th>
                                        <th rowSpan={2} style={{ width: 90 }}>Miễn giảm</th>
                                        <th rowSpan={2} style={{ width: 90 }}>Phải nộp</th>
                                    </>
                                )}
                                {requireChot && (
                                    <th rowSpan={2} style={{ width: 100 }}>Trạng thái</th>
                                )}
                                <th rowSpan={2} style={{ width: 80 }}>#</th>
                            </tr>
                            <tr>
                                <th>Thời gian</th>
                                <th>Thứ</th>
                                <th>Tiết</th>
                                <th>Phòng</th>
                            </tr>
                        </thead>
                        <tbody>
                            {myLoading && registeredList.length === 0 ? (
                                renderLoadingRow((hasTuition ? 16 : 13) - (requireChot ? 0 : 1))
                            ) : registeredList.length === 0 ? (
                                <tr>
                                    <td colSpan={(hasTuition ? 16 : 13) - (requireChot ? 0 : 1)} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                                        Bạn chưa đăng ký lớp nào trong đợt này.
                                    </td>
                                </tr>
                            ) : (
                                registeredList.flatMap((row, idx) => {
                                    const isCancelling = cancellingClassIds.includes(row.id_lop_tc);
                                    const cellsBefore = [
                                        idx + 1,
                                        row.ma_hoc_phan ?? "—",
                                        <Box>
                                            <p style={{ fontWeight: 600, marginBottom: 4 }}>
                                                {row.ten_hoc_phan ?? "—"}
                                            </p>
                                            {row.hinh_thuc_dang_ky_text && (
                                                <span className="old-badge old-badge-primary">
                                                    {row.hinh_thuc_dang_ky_text}
                                                </span>
                                            )}
                                            {row.tinh_chat_lop_text && (
                                                <span className="old-badge old-badge-success">
                                                    {row.tinh_chat_lop_text}
                                                </span>
                                            )}
                                        </Box>,
                                        row.so_tin_chi ?? "—",
                                        row.ten_lop_hp ?? `#${row.id_lop_tc}`,
                                        row.loai_lop ?? "—",
                                    ];
                                    const cellsAfter = [
                                        // 3 cột tiền — snapshot từ Registration row (đã chốt tại register-time).
                                        // KHÔNG dùng so_tien của ClassListItem hiện tại vì policy/đơn giá có thể đổi.
                                        ...(hasTuition ? [
                                            <span style={{ display: "block", textAlign: "right" }}>
                                                {(row.so_tien ?? 0).toLocaleString("vi-VN")}
                                            </span>,
                                            <span style={{ display: "block", textAlign: "right" }}>
                                                {(row.so_tien_mien_giam ?? 0).toLocaleString("vi-VN")}
                                            </span>,
                                            <span style={{ display: "block", textAlign: "right" }}>
                                                {(row.so_tien_phai_nop ?? 0).toLocaleString("vi-VN")}
                                            </span>,
                                        ] : []),
                                        // Cột trạng thái duyệt chỉ có nghĩa khi đợt yêu cầu chốt/duyệt
                                        // (yeu_cau_chot_du_lieu). Đợt không yêu cầu → ẩn để SV không
                                        // thấy "Chưa duyệt" gây hiểu nhầm.
                                        ...(requireChot ? [
                                            row.duyet_dang_ky ? (
                                                <span className="text-success">Đã duyệt</span>
                                            ) : (
                                                <span className="text-danger">Chưa duyệt</span>
                                            ),
                                        ] : []),
                                        !isChotDuLieu && !row.duyet_dang_ky ? (
                                            <button
                                                className="old-btn old-btn-outline-danger"
                                                disabled={isCancelling || !!registerGateReason}
                                                title={registerGateReason || "Huỷ đăng ký lớp này"}
                                                onClick={() => setConfirmCancel(row)}
                                            >
                                                {isCancelling ? <Spinner size="small" /> : "Hủy"}
                                            </button>
                                        ) : (
                                            <span style={{ color: "#888" }}>—</span>
                                        ),
                                    ];
                                    return renderTKBRows(
                                        row.thoiKhoaBieus,
                                        cellsBefore,
                                        cellsAfter,
                                        `reg-${row.id}`,
                                        undefined,
                                        row.giao_vien
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </Box>

            {/* ============ SECTION 2 + 3 + 4: REGISTERING ============
                 nhomLop === "" → split B2 (listSubject) + B3 (listDetail)
                 nhomLop !== "" → B4 (listDetailWithSubject) fullwidth, replace B2+3
              ============================================================ */}
            {!isChotDuLieu && (
                <Box id="Registering">
                    {enforceTimeWindow &&
                    queueStatus !== eReducerStatusBase.is_completed &&
                    queueStatus !== eReducerStatusBase.is_error ? (
                        // Chặn toàn bộ UI đăng ký khi /queue/status chưa trả snapshot thời gian.
                        // Tránh hiển thị nút "Chọn"/"Đăng ký" rồi mới chặn ở backend — UX không nhất quán
                        // (button enable nhưng backend reject OUT_OF_TIME_WINDOW).
                        <Box
                            sx={{
                                textAlign: "center",
                                py: 5,
                                bg: "#fff",
                                border: "1px solid #eee",
                                borderRadius: 2,
                            }}
                        >
                            <Spinner size="large" />
                            <Text as="p" sx={{ mt: 2, color: "#666", fontSize: 1 }}>
                                Đang tải thông tin thời gian đăng ký từ cổng. Vui lòng đợi...
                            </Text>
                        </Box>
                    ) : (
                    <>
                    {/* Filter bar — apply cho cả B2+3 lẫn B4 */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 2,
                            flexWrap: "wrap",
                            mb: 2,
                        }}
                    >
                        <div className="old-section-title" style={{ flex: 1, minWidth: 220 }}>
                            <span>
                                {nhomLop === ""
                                    ? "Danh sách học phần đăng ký học"
                                    : "Danh sách lớp tín chỉ theo nhóm lớp"}
                            </span>
                        </div>
                        <select
                            value={hinhThuc}
                            onChange={(e) => setHinhThuc(Number(e.target.value))}
                            className="old-form-control"
                            style={{ width: "auto", display: "inline-block" }}
                        >
                            <option value={0}>Tất cả hình thức</option>
                            <option value={1}>Học chính khóa</option>
                            <option value={2}>Học lại, học cải thiện điểm, học bù (lớp riêng)</option>
                            <option value={3}>Học lại, học cải thiện điểm, học bù (lớp ghép)</option>
                        </select>
                        <select
                            value={nhomLop}
                            onChange={(e) => {
                                const next = e.target.value;
                                setNhomLop(next);
                                setSelectedSubjectKey(null);
                                // Auto refresh slot Bảng 4 khi user CHỌN nhóm lớp (next !== "")
                                if (next !== "") {
                                    const ids = list
                                        .filter(c => String(c.stu_nhomlop_ids ?? 0) === next)
                                        .map(c => c.classId);
                                    refreshSlots(ids);
                                }
                            }}
                            className="old-form-control"
                            style={{ width: "auto", display: "inline-block" }}
                        >
                            <option value="">-- Chọn nhóm lớp --</option>
                            {nhomLopOptions.map(nl => (
                                <option key={nl.id} value={String(nl.id)}>
                                    {nl.ten_nhom}
                                </option>
                            ))}
                        </select>
                        {/* Combobox đợt học — chỉ hiện khi đợt mở có nhiều hơn 1 ky_dang_ky.
                            Không chọn (0) = hiển thị hết các đợt. */}
                        {showKyDangKyCombo && (
                            <select
                                value={kyDangKy}
                                onChange={(e) => handleChangeKyDangKy(Number(e.target.value))}
                                className="old-form-control"
                                style={{ width: "auto", display: "inline-block" }}
                            >
                                <option value={0}>-- Tất cả đợt học --</option>
                                {kyDangKyOptions.map(k => (
                                    <option key={k.ky_dang_ky} value={k.ky_dang_ky}>
                                        {k.label}
                                    </option>
                                ))}
                            </select>
                        )}
                    </Box>

                    {nhomLop === "" ? (
                    <Box
                        sx={{
                            display: "flex",
                            gap: "15px",
                            flexDirection: ["column", "column", "row"],
                        }}
                    >
                    {/* ----- listSubject 55% ----- */}
                    <Box id="listSubject" sx={{ width: ["100%", "100%", "55%"], bg: "#fff" }}>
                        <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "60vh" }}>
                            <table className="old-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 50 }}>STT</th>
                                        <th style={{ width: 60 }}>Kỳ thứ</th>
                                        <th>Học phần</th>
                                        <th style={{ width: 60 }}>Số TC</th>
                                        {showNhomCols && (
                                            <>
                                                <th style={{ width: 90 }}>Nhóm chung</th>
                                                <th style={{ width: 90 }}>Nhóm tự chọn</th>
                                            </>
                                        )}
                                        <th style={{ width: 200 }}>Ràng buộc</th>
                                        {hasTuition && (
                                            <>
                                                <th style={{ width: 90 }}>Số tiền</th>
                                                <th style={{ width: 90 }}>Miễn giảm</th>
                                                <th style={{ width: 90 }}>Phải nộp</th>
                                            </>
                                        )}
                                        <th style={{ width: 100 }}>#</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {classesLoading && filteredSubjects.length === 0 ? (
                                        renderLoadingRow((hasTuition ? 11 : 8) - (showNhomCols ? 0 : 2))
                                    ) : filteredSubjects.length === 0 ? (
                                        <tr>
                                            <td colSpan={(hasTuition ? 11 : 8) - (showNhomCols ? 0 : 2)} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                                                Không có học phần nào.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredSubjects.map((s, idx) => {
                                            // KHÔNG validate canRegister ở Bảng 2 (môn) — vì 1 môn có nhiều lớp,
                                            // canRegister của lớp đầu tiên không đại diện cho cả nhóm.
                                            // Validate per-lớp ở Bảng 3 (cột # nút "Đăng ký" disabled khi !canRegister).
                                            const isActive = selectedSubjectKey === s.subjectKey;

                                            return (
                                                <tr
                                                    key={s.subjectKey}
                                                    className={isActive ? "old-row-active" : undefined}
                                                >
                                                    <td className="text-center">{idx + 1}</td>
                                                    <td className="text-center">{s.ky_thu ?? "—"}</td>
                                                    <td>
                                                        <p style={{ fontWeight: 600, marginBottom: 4 }}>
                                                            {s.ky_hieu ? `${s.ky_hieu} - ` : ""}
                                                            {s.ten_mon}
                                                        </p>
                                                        {s.tinh_chat_lop_text && (
                                                            <span className="old-badge old-badge-primary">
                                                                {s.tinh_chat_lop_text}
                                                            </span>
                                                        )}
                                                        {/* {s.hinh_thuc_dang_ky_text && (
                                                            <span className="old-badge old-badge-success">
                                                                {s.hinh_thuc_dang_ky_text}
                                                            </span>
                                                        )} */}
                                                    </td>
                                                    <td className="text-center">{s.so_tin_chi}</td>
                                                    {showNhomCols && (
                                                        <>
                                                            <td className="text-center">
                                                                {/* Phase 1: backend trả "" (ESS chưa expose nhom_TuChon_Chung). Phase 2 sẽ có data. */}
                                                                {s.nhom_TuChon_Chung ?? ""}
                                                            </td>
                                                            <td className="text-center">{s.nhom_tu_chon_text ?? ""}</td>
                                                        </>
                                                    )}
                                                    <td>{s.rang_buoc_text ?? ""}</td>
                                                    {hasTuition && (() => {
                                                        // Cùng môn = cùng tiền cho cùng SV (đơn giá theo id_lop SV,
                                                        // tinh_chat theo lịch sử SV với môn — class-independent). Lấy
                                                        // class đầu tiên có so_tien > 0 (fallback class[0]) để tránh
                                                        // 0đ nếu môn có 1 lớp đặc biệt xếp trước.
                                                        const sample = s.classes.find(c => (c.so_tien ?? 0) > 0) ?? s.classes[0];
                                                        const tien = sample?.so_tien ?? 0;
                                                        const giam = sample?.so_tien_mien_giam ?? 0;
                                                        const phai = sample?.so_tien_phai_nop ?? 0;
                                                        return (
                                                            <>
                                                                <td className="text-right">{tien.toLocaleString("vi-VN")}</td>
                                                                <td className="text-right">{giam.toLocaleString("vi-VN")}</td>
                                                                <td className="text-right">{phai.toLocaleString("vi-VN")}</td>
                                                            </>
                                                        );
                                                    })()}
                                                    <td className="text-center">
                                                        {isActive ? (
                                                            <button
                                                                className="old-btn old-btn-outline-danger"
                                                                onClick={() => handleSelectSubject(s.subjectKey)}
                                                            >
                                                                Bỏ chọn
                                                            </button>
                                                        ) : (
                                                            <button
                                                                className="old-btn old-btn-outline-primary"
                                                                onClick={() => handleSelectSubject(s.subjectKey)}
                                                            >
                                                                Chọn
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Box>

                    {/* ----- listDetail 50% ----- */}
                    <Box id="listDetail" sx={{ width: ["100%", "100%", "50%"] }}>
                        <div className="old-section-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span>DANH SÁCH CÁC LỚP TÍN CHỈ ĐƯỢC LỰA CHỌN ĐĂNG KÝ</span>
                            {selectedSubject && (
                                <button
                                    className="old-btn old-btn-outline-primary"
                                    onClick={handleRefreshSlots}
                                    title="Tải lại số chỗ trống"
                                    style={{ fontSize: 12, padding: "4px 10px" }}
                                >
                                    <i className="fas fa-sync-alt" /> Tải lại
                                </button>
                            )}
                        </div>

                        <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "60vh" }}>
                            <table className="old-table">
                                <thead>
                                    <tr>
                                        <th rowSpan={2} style={{ width: 50 }}>STT</th>
                                        <th rowSpan={2}>Tên lớp</th>
                                        <th colSpan={4}>Thời khóa biểu</th>
                                        <th rowSpan={2} style={{ width: 130 }}>Giáo viên</th>
                                        <th rowSpan={2} style={{ width: 80 }}>Còn trống</th>
                                        {hasTuition && (
                                            <>
                                                <th rowSpan={2} style={{ width: 90 }}>Số tiền</th>
                                                <th rowSpan={2} style={{ width: 90 }}>Miễn giảm</th>
                                                <th rowSpan={2} style={{ width: 90 }}>Phải nộp</th>
                                            </>
                                        )}
                                        <th rowSpan={2} style={{ width: 80 }}>#</th>
                                    </tr>
                                    <tr>
                                        <th>Thời gian</th>
                                        <th>Thứ</th>
                                        <th>Tiết</th>
                                        <th>Phòng</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!selectedSubject ? (
                                        <tr>
                                            <td colSpan={hasTuition ? 12 : 9} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                                                Chọn học phần đăng ký học
                                            </td>
                                        </tr>
                                    ) : (
                                        selectedSubjectClasses.flatMap((c, idx) => {
                                            const isFull = c.slotsLeft <= 0;
                                            const isLocked = isFull || !c.canRegister || !!registerGateReason;
                                            const isPending = pendingClassIds.includes(c.classId);
                                            const isConfirmed = confirmedClassIds.includes(c.classId);
                                            const isAlreadyRegistered = myList.some(
                                                r => r.id_lop_tc === c.classId && r.trang_thai === 1
                                            );
                                            const hasLth = !!(c.listThucHanh && c.listThucHanh.length > 0);
                                            const isExpanded = expandedSub?.classId === c.classId;

                                            const cellsBefore = [
                                                idx + 1,
                                                <Box>
                                                    <p style={{ fontWeight: 600, marginBottom: 4 }}>
                                                        {c.ten_lop_hp}
                                                    </p>
                                                    {c.hinh_thuc_dang_ky_text && (
                                                        <span className="old-badge old-badge-primary">
                                                            {c.hinh_thuc_dang_ky_text}
                                                        </span>
                                                    )}
                                                </Box>,
                                            ];

                                            // Rule 12: SV đã đăng ký lớp khác cùng môn → ẩn nút Đăng ký, show text.
                                            const isSameMonRegistered = c.reasonCode === "ALREADY_REGISTERED_SAME_MON";

                                            // Multi-state button cột # (rule Old project):
                                            //   Đã ĐK / Pending → giữ nguyên trạng thái
                                            //   isSameMonRegistered → text "Đã ĐK môn này" (rule 12)
                                            //   hasLth → "Chọn LTH" toggle expand sub-table thực hành
                                            //   else   → "Đăng ký" (đăng ký đơn lẻ; môn song hành cũng đi lối này)
                                            const finBlock = getFinancialBlock(c, financial);
                                            const actionButton = isAlreadyRegistered || isConfirmed ? (
                                                <span className="text-success">
                                                    <i className="fas fa-check" /> Đã ĐK
                                                </span>
                                            ) : isPending ? (
                                                <button className="old-btn old-btn-outline-primary" disabled>
                                                    <Spinner size="small" />
                                                </button>
                                            ) : isSameMonRegistered ? (
                                                <span style={{ color: "#888", fontStyle: "italic", fontSize: 12 }}>
                                                    Đã ĐK môn này
                                                </span>
                                            ) : c.reasonCode === "NHOM_TU_CHON_FULL" ? (
                                                // Cap nhóm tự chọn đã đủ → ẩn nút Đăng ký, hiện lý do inline.
                                                <span style={{ color: "#a30f26", fontStyle: "italic", fontSize: 12 }}>
                                                    {c.message ?? "Đã đủ nhóm tự chọn"}
                                                </span>
                                            ) : finBlock ? (
                                                // FE gate tài chính (strict mode mirror backend EligibilityService):
                                                // ẩn hoàn toàn nút Đăng ký + Chọn LTH/SH khi SV không có hồ sơ TC
                                                // hoặc không đủ số dư. Banner ở widget đã giải thích lý do tổng quát.
                                                <span style={{ color: "#a30f26", fontStyle: "italic", fontSize: 12 }}>
                                                    {finBlock}
                                                </span>
                                            ) : hasLth ? (
                                                withLockedRegisterTooltip(
                                                    isLocked,
                                                    c,
                                                    isFull,
                                                    <button
                                                        className={`old-btn ${isExpanded ? "old-btn-outline-danger" : "old-btn-outline-primary"}`}
                                                        disabled={isLocked}
                                                        onClick={() => setExpandedSub(prev =>
                                                            prev?.classId === c.classId ? null : { classId: c.classId }
                                                        )}
                                                    >
                                                        {isExpanded ? "Đóng" : "Chọn LTH"}
                                                    </button>,
                                                    registerGateReason
                                                )
                                            ) : (
                                                withLockedRegisterTooltip(
                                                    isLocked,
                                                    c,
                                                    isFull,
                                                    <button
                                                        className="old-btn old-btn-outline-primary"
                                                        disabled={isLocked}
                                                        onClick={() => handleRegister(c.classId)}
                                                    >
                                                        Đăng ký
                                                    </button>,
                                                    registerGateReason
                                                )
                                            );

                                            const cellsAfter = [
                                                isFull ? (
                                                    <span className="text-danger">Hết</span>
                                                ) : (
                                                    <strong>{c.slotsLeft}</strong>
                                                ),
                                                ...(hasTuition ? [
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien_mien_giam ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien_phai_nop ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                ] : []),
                                                actionButton,
                                            ];

                                            const hasConflict = !!(c.iD_lops_TrungLich && c.iD_lops_TrungLich.length > 0);
                                            const highlightClass = isAlreadyRegistered
                                                ? undefined
                                                : (isFull || hasConflict ? "old-row-locked" : undefined);

                                            const tkbRows = renderTKBRows(
                                                c.thoiKhoaBieus,
                                                cellsBefore,
                                                cellsAfter,
                                                `cls-${c.classId}`,
                                                highlightClass,
                                                c.giao_vien
                                            );

                                            // Phase 2A: render sub-table khi expanded.
                                            // onPickPair = handleRegister single (Phase 2C sẽ thay thành register pair atomic).
                                            const subTable = isExpanded
                                                ? renderSubTable(
                                                    c.listThucHanh!,
                                                    c.classId,
                                                    hasTuition ? 12 : 9,
                                                    myList,
                                                    pendingClassIds,
                                                    (parentId, childId) =>
                                                        dispatch(rootActions.registration.REGISTER_PAIR_START({
                                                            parentClassId: parentId,
                                                            childClassId: childId,
                                                        })),
                                                    hasTuition,
                                                    financial,
                                                    registerGateReason
                                                )
                                                : null;

                                            return [tkbRows, subTable].filter(Boolean);
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {selectedSubject && (
                            <Text
                                as="p"
                                sx={{
                                    textAlign: "right",
                                    fontStyle: "italic",
                                    mt: "3px",
                                    fontSize: 1,
                                    color: "#666",
                                }}
                            >
                                Lưu ý: Màu vàng là trùng thời khóa biểu hoặc hết chỗ đăng ký - sẽ không đăng ký được
                            </Text>
                        )}
                    </Box>
                    </Box>
                    ) : (
                    /* ===================== B4: listDetailWithSubject (15 cột combined) ===================== */
                    <Box id="listDetailWithSubject" sx={{ width: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                            <button
                                className="old-btn old-btn-outline-primary"
                                onClick={handleRefreshSlots}
                                title="Tải lại số chỗ trống"
                                style={{ fontSize: 12, padding: "4px 10px" }}
                            >
                                <i className="fas fa-sync-alt" /> Tải lại
                            </button>
                        </div>
                        <div style={{ overflowX: "auto" }}>
                            <table className="old-table">
                                <thead>
                                    <tr>
                                        <th rowSpan={2} style={{ width: 50 }}>STT</th>
                                        <th rowSpan={2} style={{ width: 60 }}>Kỳ thứ</th>
                                        <th rowSpan={2}>Học phần</th>
                                        <th rowSpan={2} style={{ width: 60 }}>Số TC</th>
                                        {showNhomCols && (
                                            <>
                                                <th rowSpan={2} style={{ width: 90 }}>Nhóm chung</th>
                                                <th rowSpan={2} style={{ width: 90 }}>Nhóm tự chọn</th>
                                            </>
                                        )}
                                        <th rowSpan={2} style={{ width: 200 }}>Ràng buộc</th>
                                        <th rowSpan={2} style={{ width: 130 }}>Tên lớp</th>
                                        <th colSpan={4}>Thời khóa biểu</th>
                                        <th rowSpan={2} style={{ width: 130 }}>Giáo viên</th>
                                        <th rowSpan={2} style={{ width: 80 }}>Còn trống</th>
                                        {hasTuition && (
                                            <>
                                                <th rowSpan={2} style={{ width: 90 }}>Số tiền</th>
                                                <th rowSpan={2} style={{ width: 90 }}>Miễn giảm</th>
                                                <th rowSpan={2} style={{ width: 90 }}>Phải nộp</th>
                                            </>
                                        )}
                                        <th rowSpan={2} style={{ width: 80 }}>#</th>
                                    </tr>
                                    <tr>
                                        <th>Thời gian</th>
                                        <th>Thứ</th>
                                        <th>Tiết</th>
                                        <th>Phòng</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {classesLoading && filteredCombinedClasses.length === 0 ? (
                                        renderLoadingRow((hasTuition ? 18 : 15) - (showNhomCols ? 0 : 2))
                                    ) : filteredCombinedClasses.length === 0 ? (
                                        <tr>
                                            <td colSpan={(hasTuition ? 18 : 15) - (showNhomCols ? 0 : 2)} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                                                Không có lớp nào trong nhóm này.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredCombinedClasses.flatMap((c, idx) => {
                                            const isFull = c.slotsLeft <= 0;
                                            const isLocked = isFull || !c.canRegister || !!registerGateReason;
                                            const isPending = pendingClassIds.includes(c.classId);
                                            const isConfirmed = confirmedClassIds.includes(c.classId);
                                            const isAlreadyRegistered = myList.some(
                                                r => r.id_lop_tc === c.classId && r.trang_thai === 1
                                            );
                                            const hasLth = !!(c.listThucHanh && c.listThucHanh.length > 0);
                                            const isExpanded = expandedSub?.classId === c.classId;

                                            const cellsBefore = [
                                                idx + 1,
                                                c.ky_thu ?? "—",
                                                <Box>
                                                    <p style={{ fontWeight: 600, marginBottom: 4 }}>
                                                        {c.ky_hieu ? `${c.ky_hieu} - ` : ""}
                                                        {c.ten_mon}
                                                    </p>
                                                    {c.tinh_chat_lop_text && (
                                                        <span className="old-badge old-badge-primary">
                                                            {c.tinh_chat_lop_text}
                                                        </span>
                                                    )}
                                                    {c.hinh_thuc_dang_ky_text && (
                                                        <span className="old-badge old-badge-success">
                                                            {c.hinh_thuc_dang_ky_text}
                                                        </span>
                                                    )}
                                                </Box>,
                                                c.so_tin_chi,
                                                ...(showNhomCols ? [
                                                    c.nhom_TuChon_Chung ?? "",
                                                    c.nhom_tu_chon_text ?? "",
                                                ] : []),
                                                c.rang_buoc_text ?? "",
                                                c.ten_lop_hp,
                                            ];

                                            // Rule 12: SV đã đăng ký lớp khác cùng môn → ẩn nút Đăng ký, show text.
                                            const isSameMonRegistered = c.reasonCode === "ALREADY_REGISTERED_SAME_MON";

                                            const finBlock = getFinancialBlock(c, financial);
                                            const actionButton = isAlreadyRegistered || isConfirmed ? (
                                                <span className="text-success">
                                                    <i className="fas fa-check" /> Đã ĐK
                                                </span>
                                            ) : isPending ? (
                                                <button className="old-btn old-btn-outline-primary" disabled>
                                                    <Spinner size="small" />
                                                </button>
                                            ) : isSameMonRegistered ? (
                                                <span style={{ color: "#888", fontStyle: "italic", fontSize: 12 }}>
                                                    Đã ĐK môn này
                                                </span>
                                            ) : c.reasonCode === "NHOM_TU_CHON_FULL" ? (
                                                // Cap nhóm tự chọn đã đủ → ẩn nút Đăng ký, hiện lý do inline.
                                                <span style={{ color: "#a30f26", fontStyle: "italic", fontSize: 12 }}>
                                                    {c.message ?? "Đã đủ nhóm tự chọn"}
                                                </span>
                                            ) : finBlock ? (
                                                // FE gate tài chính (strict mode mirror backend EligibilityService):
                                                // ẩn hoàn toàn nút Đăng ký + Chọn LTH/SH khi SV không có hồ sơ TC
                                                // hoặc không đủ số dư. Banner ở widget đã giải thích lý do tổng quát.
                                                <span style={{ color: "#a30f26", fontStyle: "italic", fontSize: 12 }}>
                                                    {finBlock}
                                                </span>
                                            ) : hasLth ? (
                                                withLockedRegisterTooltip(
                                                    isLocked,
                                                    c,
                                                    isFull,
                                                    <button
                                                        className={`old-btn ${isExpanded ? "old-btn-outline-danger" : "old-btn-outline-primary"}`}
                                                        disabled={isLocked}
                                                        onClick={() => setExpandedSub(prev =>
                                                            prev?.classId === c.classId ? null : { classId: c.classId }
                                                        )}
                                                    >
                                                        {isExpanded ? "Đóng" : "Chọn LTH"}
                                                    </button>,
                                                    registerGateReason
                                                )
                                            ) : (
                                                withLockedRegisterTooltip(
                                                    isLocked,
                                                    c,
                                                    isFull,
                                                    <button
                                                        className="old-btn old-btn-outline-primary"
                                                        disabled={isLocked}
                                                        onClick={() => handleRegister(c.classId)}
                                                    >
                                                        Đăng ký
                                                    </button>,
                                                    registerGateReason
                                                )
                                            );

                                            const cellsAfter = [
                                                isFull ? (
                                                    <span className="text-danger">Hết</span>
                                                ) : (
                                                    <strong>{c.slotsLeft}</strong>
                                                ),
                                                ...(hasTuition ? [
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien_mien_giam ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien_phai_nop ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                ] : []),
                                                actionButton,
                                            ];

                                            const hasConflict = !!(c.iD_lops_TrungLich && c.iD_lops_TrungLich.length > 0);
                                            const highlightClass = isAlreadyRegistered
                                                ? undefined
                                                : (isFull || hasConflict ? "old-row-locked" : undefined);

                                            const tkbRows = renderTKBRows(
                                                c.thoiKhoaBieus,
                                                cellsBefore,
                                                cellsAfter,
                                                `combined-${c.classId}`,
                                                highlightClass,
                                                c.giao_vien
                                            );

                                            // Bảng 4 colSpan = 15 (18 khi có cột tiền), trừ 2 khi ẩn cột Nhóm chung/Nhóm tự chọn.
                                            const subTable = isExpanded
                                                ? renderSubTable(
                                                    c.listThucHanh!,
                                                    c.classId,
                                                    (hasTuition ? 18 : 15) - (showNhomCols ? 0 : 2),
                                                    myList,
                                                    pendingClassIds,
                                                    (parentId, childId) =>
                                                        dispatch(rootActions.registration.REGISTER_PAIR_START({
                                                            parentClassId: parentId,
                                                            childClassId: childId,
                                                        })),
                                                    hasTuition,
                                                    financial,
                                                    registerGateReason
                                                )
                                                : null;

                                            return [tkbRows, subTable].filter(Boolean);
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Text
                            as="p"
                            sx={{
                                textAlign: "right",
                                fontStyle: "italic",
                                mt: "3px",
                                fontSize: 1,
                                color: "#666",
                            }}
                        >
                            Lưu ý: Màu vàng là trùng thời khóa biểu hoặc hết chỗ đăng ký - sẽ không đăng ký được
                        </Text>
                    </Box>
                    )}
                    </>
                    )}
                </Box>
            )}

            {/* Confirm dialogs */}
            {confirmCancel && (
                <ConfirmationDialog
                    title="Xác nhận hủy đăng ký"
                    onClose={(g) => (g === "confirm" ? handleCancelConfirm() : setConfirmCancel(null))}
                    confirmButtonContent="Xác nhận hủy"
                    confirmButtonType="danger"
                    cancelButtonContent="Đóng"
                >
                    Bạn có chắc muốn hủy đăng ký lớp{" "}
                    <strong>{confirmCancel.ten_lop_hp ?? `#${confirmCancel.id_lop_tc}`}</strong>?
                    {(() => {
                        const partner = findPairPartner(confirmCancel, myList);
                        return partner ? (
                            <Box sx={{ mt: 2, p: "8px 10px", bg: "#fff4e5", border: "1px solid #ffd9a8", borderRadius: 2, fontSize: 1 }}>
                                <i className="fas fa-link" style={{ color: "#c26a00" }} />{" "}
                                Lớp này đăng ký kèm <strong>{partner.loai_lop} {partner.ten_lop_hp ?? `#${partner.id_lop_tc}`}</strong>{" "}
                                — <strong>cả lý thuyết + thực hành sẽ được hủy cùng nhau</strong>.
                            </Box>
                        ) : null;
                    })()}
                </ConfirmationDialog>
            )}

            {confirmChot && (
                <ConfirmationDialog
                    title="Xác nhận lưu dữ liệu đăng ký"
                    onClose={(g) => (g === "confirm" ? handleChotDuLieuConfirm() : setConfirmChot(false))}
                    confirmButtonContent="Lưu"
                    confirmButtonType="danger"
                    cancelButtonContent="Đóng"
                >
                    Lưu ý: Sinh viên tự chịu trách nhiệm với kết quả đăng ký tín chỉ. Bạn đã chắc chắn muốn lưu lại kết quả đăng ký không? Sau khi bấm lưu, sinh viên không được phép thay đổi kết quả đăng ký.
                </ConfirmationDialog>
            )}
        </PageLayout>
    );
};

export default ClassesPage;
